import { useContext } from "react";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TimePicker as MuiTimePicker } from "@mui/x-date-pickers/TimePicker";
import { DynamicContext } from "../../Context/DynamicContext";

const CustomTimePicker = ({
  selectedTime,
  onChange,
  placeholderText = "Select time",
  style = {},
  fullWidth = false,
  width = "100%",
}) => {
  const { isDarkMode } = useContext(DynamicContext);
  const value = selectedTime ? moment(selectedTime, "HH:mm") : null;
  const pickerBackground = isDarkMode
    ? "var(--sidepopup-background, #17181f)"
    : "var(--body__background, #ffffff)";
  const pickerSurface = isDarkMode
    ? "var(--body__background, #111217)"
    : "#ffffff";
  const pickerText = "var(--header__color, #111827)";
  const pickerMutedText = isDarkMode ? "#a8adbd" : "#6b7280";
  const pickerBorder = "var(--border__color, #e2e4e9)";

  const pickerPaperSx = {
    backgroundColor: pickerBackground,
    color: pickerText,
    border: `1px solid ${pickerBorder}`,
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: isDarkMode
      ? "0 12px 32px rgba(0, 0, 0, 0.55)"
      : "0 12px 32px rgba(0, 0, 0, 0.18)",
    "& .MuiPickersLayout-root": {
      backgroundColor: pickerBackground,
      color: pickerText,
    },
    "& .MuiMultiSectionDigitalClock-root": {
      backgroundColor: pickerBackground,
      borderColor: pickerBorder,
      borderBottom: `1px solid ${pickerBorder}`,
    },
    "& .MuiMultiSectionDigitalClockSection-root": {
      backgroundColor: pickerSurface,
      borderColor: pickerBorder,
      scrollbarWidth: "thin",
      scrollbarColor: isDarkMode
        ? "#6860ee rgba(255,255,255,0.08)"
        : "#a7a2f5 rgba(0,0,0,0.08)",
      "&::after": {
        backgroundColor: pickerSurface,
      },
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: isDarkMode
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.06)",
        borderRadius: "999px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: isDarkMode ? "#6860ee" : "#a7a2f5",
        borderRadius: "999px",
      },
    },
    "& .MuiMenuItem-root": {
      color: pickerText,
      backgroundColor: pickerSurface,
      borderRadius: "8px",
      minHeight: "34px",
      margin: "2px 0",
      justifyContent: "center",
      fontSize: "14px",
      fontWeight: 600,
      "&:hover": {
        backgroundColor: isDarkMode
          ? "rgba(104, 96, 238, 0.22)"
          : "rgba(104, 96, 238, 0.08)",
      },
      "&.Mui-selected": {
        color: "#ffffff",
        backgroundColor: "#6860ee",
      },
      "&.Mui-selected:hover": {
        backgroundColor: "#5b54df",
      },
      "&.Mui-disabled": {
        color: pickerMutedText,
        opacity: 0.65,
      },
    },
    "& .MuiDialogActions-root": {
      backgroundColor: pickerBackground,
      borderTop: `1px solid ${pickerBorder}`,
      padding: "10px 14px",
    },
    "& .MuiButton-root": {
      color: "#ffffff",
      backgroundColor: "#6860ee",
      borderRadius: "8px",
      padding: "6px 16px",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": {
        backgroundColor: "#5b54df",
      },
    },
  };

  const handleChange = (newValue) => {
    if (newValue && moment.isMoment(newValue) && newValue.isValid()) {
      onChange(newValue.format("HH:mm"));
    } else {
      onChange("");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div style={{ width: fullWidth ? "100%" : width, ...style }}>
        <MuiTimePicker
          value={value}
          onChange={handleChange}
          ampm
          minutesStep={15}
          slotProps={{
            textField: {
              placeholder: placeholderText,
              size: "small",
              InputLabelProps: { shrink: false },
              sx: {
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: isDarkMode
                    ? "var(--sidepopup-background, #17181f)"
                    : "var(--input__background, #fafafa)",
                  color: pickerText,
                  "& fieldset": {
                    borderColor: pickerBorder,
                  },
                  "&:hover fieldset": {
                    borderColor: "#6860ee",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#6860ee",
                  },
                  "& input": {
                    color: pickerText,
                    WebkitTextFillColor: pickerText,
                  },
                  "& input::placeholder": {
                    color: pickerMutedText,
                    opacity: 1,
                  },
                  "& .MuiSvgIcon-root": {
                    color: pickerMutedText,
                  },
                },
              },
            },
            popper: {
              sx: pickerPaperSx,
            },
            desktopPaper: {
              sx: pickerPaperSx,
            },
            mobilePaper: {
              sx: pickerPaperSx,
            },
            dialog: {
              sx: {
                "& .MuiPaper-root": pickerPaperSx,
              },
            },
          }}
        />
      </div>
    </LocalizationProvider>
  );
};

export default CustomTimePicker;
